import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Visibility, VisibilityOff, Save } from "@material-ui/icons";
import { toast } from "react-toastify";
import useSettings from "../../hooks/useSettings";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  infoText: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  formField: {
    marginBottom: theme.spacing(1),
  },
  buttonContainer: {
    marginTop: theme.spacing(3),
    display: "flex",
    justifyContent: "flex-end",
  },
  webhookUrlBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.type === "dark" ? "#333" : "#f5f5f5",
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
  },
  webhookUrl: {
    fontFamily: "monospace",
    fontSize: "0.9rem",
    wordBreak: "break-all",
    color: theme.palette.primary.main,
  },
}));

const CloudApiSettings = () => {
  const classes = useStyles();
  const { getAll, update } = useSettings();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [showVerifyToken, setShowVerifyToken] = useState(false);

  const [appSecret, setAppSecret] = useState("");
  const [verifyToken, setVerifyToken] = useState("");
  const [apiVersion, setApiVersion] = useState("v25.0");
  const [facebookAppId, setFacebookAppId] = useState("");
  const [configId, setConfigId] = useState("");
  const [managedByServer, setManagedByServer] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const webhookUrl = `${backendUrl}/webhook/cloudapi`;

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      // Quando o app da Meta está configurado no .env do servidor, ele tem
      // precedência sobre tudo que estiver preenchido aqui.
      try {
        const { data } = await api.get("/meta/app-config");
        setManagedByServer(Boolean(data?.whatsapp?.enabled));
      } catch (_err) {
        setManagedByServer(false);
      }

      const settings = await getAll();
      if (Array.isArray(settings)) {
        const secretSetting = settings.find((s) => s.key === "cloudApiAppSecret");
        const tokenSetting = settings.find((s) => s.key === "cloudApiVerifyToken");
        const versionSetting = settings.find((s) => s.key === "cloudApiVersion");

        if (secretSetting) setAppSecret(secretSetting.value || "");
        if (tokenSetting) setVerifyToken(tokenSetting.value || "");
        if (versionSetting) setApiVersion(versionSetting.value || "v25.0");

        const appIdSetting = settings.find((s) => s.key === "cloudApiFacebookAppId");
        const configIdSetting = settings.find((s) => s.key === "cloudApiConfigId");
        if (appIdSetting) setFacebookAppId(appIdSetting.value || "");
        if (configIdSetting) setConfigId(configIdSetting.value || "");
      }
    } catch (err) {
      toast.error(i18n.t("settings.cloudApi.toasts.loadError"));
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await update({ key: "cloudApiAppSecret", value: appSecret });
      await update({ key: "cloudApiVerifyToken", value: verifyToken });
      await update({ key: "cloudApiVersion", value: apiVersion });
      await update({ key: "cloudApiFacebookAppId", value: facebookAppId });
      await update({ key: "cloudApiConfigId", value: configId });
      toast.success(i18n.t("settings.cloudApi.toasts.saved"));
    } catch (err) {
      toast.error(i18n.t("settings.cloudApi.toasts.saveError"));
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <Paper className={classes.paper} elevation={0}>
        <Grid container justifyContent="center" style={{ padding: 40 }}>
          <CircularProgress />
        </Grid>
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper} elevation={0}>
      <Typography variant="h6" className={classes.sectionTitle}>
        {i18n.t("settings.cloudApi.title")}
      </Typography>

      <Typography className={classes.infoText}>
        {i18n.t("settings.cloudApi.info")}
      </Typography>

      {managedByServer && (
        <Typography
          className={classes.infoText}
          style={{ padding: 12, border: "1px solid #2e7d32", borderRadius: 4, color: "#2e7d32" }}
        >
          <strong>✅ {i18n.t("settings.instagram.managedByServer.title")}</strong>{" "}
          {i18n.t("settings.cloudApi.managedByServer")}
        </Typography>
      )}

      <Grid container spacing={2}>
        {/* === EMBEDDED SIGNUP: Facebook App ID e Config ID === */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" style={{ marginTop: 8, marginBottom: 4, color: "#1877F2", fontWeight: 600 }}>
            🔗 {i18n.t("settings.cloudApi.embeddedSignup.title")}
          </Typography>
          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
            {i18n.t("settings.cloudApi.embeddedSignup.description")}{" "}
            <a href="https://developers.facebook.com/apps/" target="_blank" rel="noopener noreferrer">
              Meta for Developers
            </a>.
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Facebook App ID"
            value={facebookAppId}
            onChange={(e) => setFacebookAppId(e.target.value)}
            fullWidth
            variant="outlined"
            margin="dense"
            helperText={i18n.t("settings.cloudApi.fields.appIdHelper")}
            placeholder={i18n.t("settings.instagram.fields.appIdPlaceholder")}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Config ID (Embedded Signup)"
            value={configId}
            onChange={(e) => setConfigId(e.target.value)}
            fullWidth
            variant="outlined"
            margin="dense"
            helperText={i18n.t("settings.cloudApi.fields.configIdHelper")}
            placeholder={i18n.t("settings.instagram.fields.appIdPlaceholder")}
          />
        </Grid>
        <Grid item xs={12}>
          <hr style={{ border: "none", borderTop: "1px solid #e0e0e0", margin: "8px 0" }} />
          <Typography variant="subtitle2" style={{ marginTop: 8, marginBottom: 4, fontWeight: 600 }}>
            ⚙️ {i18n.t("settings.cloudApi.globalSection")}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="App Secret"
            value={appSecret}
            onChange={(e) => setAppSecret(e.target.value)}
            fullWidth
            variant="outlined"
            margin="dense"
            type={showSecret ? "text" : "password"}
            helperText={i18n.t("settings.cloudApi.fields.appSecretHelper")}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowSecret(!showSecret)}
                  >
                    {showSecret ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Webhook Verify Token"
            value={verifyToken}
            onChange={(e) => setVerifyToken(e.target.value)}
            fullWidth
            variant="outlined"
            margin="dense"
            type={showVerifyToken ? "text" : "password"}
            helperText={i18n.t("settings.instagram.fields.verifyTokenHelper")}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowVerifyToken(!showVerifyToken)}
                  >
                    {showVerifyToken ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth variant="outlined" margin="dense">
            <InputLabel>{i18n.t("settings.cloudApi.fields.apiVersion")}</InputLabel>
            <Select
              value={apiVersion}
              onChange={(e) => setApiVersion(e.target.value)}
              label={i18n.t("settings.cloudApi.fields.apiVersion")}
            >
              <MenuItem value="v25.0">v25.0 ({i18n.t("settings.instagram.fields.latest")})</MenuItem>
              <MenuItem value="v24.0">v24.0</MenuItem>
              <MenuItem value="v23.0">v23.0</MenuItem>
              <MenuItem value="v22.0">v22.0</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <div className={classes.webhookUrlBox}>
        <Typography variant="subtitle2" gutterBottom>
          {i18n.t("settings.cloudApi.webhookUrl")}
        </Typography>
        <Typography className={classes.webhookUrl}>
          {webhookUrl}
        </Typography>
      </div>

      <div className={classes.buttonContainer}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={18} /> : <Save />}
        >
          {saving ? i18n.t("common.saving") : i18n.t("settings.instagram.save")}
        </Button>
      </div>
    </Paper>
  );
};

export default CloudApiSettings;
